import {useTranslation} from 'react-i18next';
import {DataActions, useArrivalContext} from 'src/context/ArrivalContext';

import {DropinType, ReasonFilterObject} from 'src/services/api/types/types';

import {PlusIcon} from 'src/components/icons/PlusIcon';
import {MinusIcon} from 'src/components/icons/MinusIcon';
import {Button} from 'src/components/form/button/Button';

import './DropinModalInitial.scss';

export function DropinModalInitial() {
    const {t} = useTranslation();

    type VisitCategory = 'vaccination' | 'travel' | 'other';

    const {dispatch, dataState} = useArrivalContext();

    const {reasonState} = dataState;
    const {category, appointmentType, numberPatients} = reasonState;

    const selectedCategoryId = category?.id ? category.id : -1;
    const selectedAppointmentTypeId = appointmentType?.id ? appointmentType.id : -1;

    const appointmentTypesFlat = dataState.categories
        .filter((cat) => cat.id === selectedCategoryId)
        .flatMap(({appointmentTypes}) => appointmentTypes);

    let minPeople = 1;
    let maxPeople = 1;
    let matchingAppointmentTypes = appointmentTypesFlat;

    if (appointmentTypesFlat.length > 0) {
        const patientsCount = matchingAppointmentTypes.map(({patients}) => patients);
        minPeople = Math.min(...patientsCount);
        maxPeople = Math.max(...patientsCount);
    }

    const numberOfPeople = numberPatients || minPeople;
    const onReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const clickedReason = parseInt(e.target.value, 10);
        const matchingTypesFlat = dataState.categories
            .filter((cat) => cat.id === selectedCategoryId)
            .flatMap(({appointmentTypes}) => appointmentTypes);

        let minPeople = 1;

        if (matchingTypesFlat.length > 0) {
            const patientsCount = matchingTypesFlat.map(({patients}) => patients);
            minPeople = Math.min(...patientsCount);
        }

        let matchingTypes = matchingTypesFlat.filter(({id}) => id === selectedAppointmentTypeId);
        if (!matchingTypes.length) {
            matchingTypes = matchingTypesFlat.filter(({patients}) => patients === numberPatients);
        }
        if (!matchingTypes.length) {
            matchingTypes = matchingTypesFlat.filter(({patients}) => patients === minPeople);
        }

        const category = dataState.categories.find(({id}) => id === clickedReason) ?? null;
        if (matchingTypes.length === 1) {
            const matchingType = matchingTypes[0];

            let category_hd = null;
            let allowed_age = null;
            if (category) {
                category_hd = category.healthDeclarationDefinitionId;
                allowed_age = {minAge: category.minAge || null, maxAge: category.maxAge || null};
            }

            dispatch({
                type: DataActions.SET_REASON_DATA,
                value: {
                    category: category,
                    numberPatients: matchingType.patients,
                    appointmentType: matchingType,
                    healthDeclarationDefinitionId: category_hd,
                    allowedAge: allowed_age
                }
            });
        } else {
            const minOrSameNumPeople =
                matchingTypesFlat.find(({patients}) => patients === numberOfPeople)?.patients || minPeople;
            dispatch({
                type: DataActions.SET_REASON_DATA,
                value: {
                    category: category,
                    numberPatients: minOrSameNumPeople,
                    appointmentType: null,
                    healthDeclarationDefinitionId: null,
                    allowedAge: null
                }
            });
        }
    };
    const onNumberPeopleClick = (amount: number) => {
        const newNumber = numberOfPeople + amount;

        const matchingTypes = dataState.categories
            .filter((cat) => cat.id === selectedCategoryId)
            .flatMap(({appointmentTypes}) => appointmentTypes)
            .filter(({patients}) => patients === newNumber);

        if (matchingTypes.length === 1) {
            const matchingType = matchingTypes[0];

            const category = dataState.categories.find((ac) => {
                return ac.appointmentTypes.some(({id}) => id === matchingType.id);
            });

            let category_hd = null;
            if (category) {
                category_hd = category.healthDeclarationDefinitionId;
            }

            dispatch({
                type: DataActions.SET_REASON_DATA,
                value: {
                    ...reasonState,
                    numberPatients: newNumber,
                    appointmentType: matchingType,
                    healthDeclarationDefinitionId: category_hd
                }
            });
        } else {
            dispatch({
                type: DataActions.SET_REASON_DATA,
                value: {
                    ...reasonState,
                    numberPatients: newNumber,
                    appointmentType: null,
                    healthDeclarationDefinitionId: null
                }
            });
        }
    };
    const selectType = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const matchingType = appointmentTypesFlat.find(({id}) => id === parseInt(e.target.value, 10)) as DropinType;

        const category = dataState.categories.find((ac) => {
            return ac.appointmentTypes.some(({id}) => id === matchingType.id);
        });

        let category_hd = null;
        let allowed_age = null;
        if (category) {
            category_hd = category.healthDeclarationDefinitionId;
            allowed_age = {minAge: category.minAge || null, maxAge: category.maxAge || null};
        }

        dispatch({
            type: DataActions.SET_REASON_DATA,
            value: {
                ...reasonState,
                appointmentType: matchingType,
                healthDeclarationDefinitionId: category_hd,
                allowedAge: allowed_age
            }
        });
    };

    const appointmentCategoryGroups = dataState.categories
        .filter((cat) => cat.id === selectedCategoryId)
        .map((cat) => {
            return {
                ...cat,
                appointmentTypes: cat.appointmentTypes.filter(({patients}) => {
                    return patients === numberOfPeople;
                })
            };
        });

    const isNumPeopleDisabled = (maxNumber: number) => {
        return selectedCategoryId === -1 || numberOfPeople === maxNumber;
    };

    const nextDisabled = selectedCategoryId === -1 || selectedAppointmentTypeId === -1;

    // data from API does not as of today (2025-08-26) come sorted, we should display something useful to end-user still
    const sortedCategories = dataState.categories.sort((a, b) => a.name.localeCompare(b.name));

    return (
        <form className='arrival-dropin-form' aria-label={t('arrival.dropin_form_aria_label')}>
            <div aria-live='polite' aria-atomic='true' className='screen-reader-only'></div>
            <span className='select-container'>
                {selectedCategoryId !== -1 && (
                    <label htmlFor='reason' className='select-with-label'>
                        {t('arrival.type_of_visit')}
                    </label>
                )}
                <select
                    id='reason'
                    value={selectedCategoryId}
                    className={`select ${selectedCategoryId !== -1 ? 'has-selection' : ''}`}
                    onChange={(e) => onReasonChange(e)}
                >
                    <option disabled value={-1}>
                        {t('arrival.type_of_visit')}
                    </option>
                    {sortedCategories.map((cat) => {
                        return (
                            <option key={cat.id} value={cat.id}>
                                {cat.name}
                            </option>
                        );
                    })}
                </select>
            </span>
            <div className='amount-input'>
                <label className='amount-input__label'>{t('arrival.people_count')}</label>
                <div className='amount-input__label__content'>
                    <button
                        disabled={isNumPeopleDisabled(minPeople)}
                        className='amount-button'
                        type='button'
                        onClick={() => onNumberPeopleClick(-1)}
                        aria-label='minus'
                    >
                        <MinusIcon />
                    </button>
                    <span className='amount-input__label__content__input'>{numberOfPeople}</span>
                    <button
                        disabled={isNumPeopleDisabled(maxPeople)}
                        className='amount-button'
                        type='button'
                        onClick={() => onNumberPeopleClick(1)}
                        aria-label='plus'
                    >
                        <PlusIcon />
                    </button>
                </div>
            </div>
            <span className='select-container'>
                {selectedAppointmentTypeId !== -1 && (
                    <label htmlFor='visit-type' className='select-with-label'>
                        {t('clinic_form.type_of_visit')}
                    </label>
                )}
                <select
                    disabled={selectedCategoryId === -1}
                    id='visit-type'
                    value={selectedAppointmentTypeId}
                    className={`select${selectedAppointmentTypeId !== -1 ? ' has-selection' : ''}`}
                    onChange={(e) => selectType(e)}
                >
                    <option disabled value={-1}>
                        {t('clinic_form.type_of_visit')}
                    </option>
                    {appointmentCategoryGroups.map((cat) => {
                        if (cat.appointmentTypes.length === 0) {
                            const maxPatients = Math.max(
                                ...(dataState.categories
                                    .find(({id}) => id === cat.id)
                                    ?.appointmentTypes.map(({patients}) => patients) || [])
                            );
                            return (
                                <optgroup key={cat.id} label={cat.name}>
                                    <option disabled key={`${cat.id}-0`} value='-1'>
                                        {t('clinic_form.max_people_needed', {count: maxPatients})}
                                    </option>
                                </optgroup>
                            );
                        }

                        return (
                            <optgroup key={cat.id} label={cat.name}>
                                {cat.appointmentTypes.map((type) => {
                                    return (
                                        <option key={`${cat.id}-${type.id}`} value={type.id}>
                                            {type.name}
                                        </option>
                                    );
                                })}
                            </optgroup>
                        );
                    })}
                </select>
            </span>
            <div style={{marginLeft: 'auto', marginTop: '32px'}}>
                <Button
                    onClick={() => {
                        dispatch({type: DataActions.NEXT_STEP});
                    }}
                    disabled={nextDisabled}
                >
                    {t('clinic_form.next')}
                </Button>
            </div>
        </form>
    );
}
